"use client";

import { useState } from "react";
import { X } from "@/components/icons";
import { toast } from "sonner";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { track } from "@/lib/analytics/tracker";
import { handleError } from "@/lib/handle-error";
import { PhoneActionRowButton } from "@/components/dashboard/dashboard-page-actions";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";

interface CancelRequestButtonProps {
  leadId: Id<"leads">;
  leadNumber?: string;
  isFreeClaim?: boolean;
  variant?: "ghost" | "outline";
  className?: string;
  asRow?: boolean;
}

function useConfirmCancel({
  leadId,
  isFreeClaim,
}: Pick<CancelRequestButtonProps, "leadId" | "isFreeClaim">) {
  const cancelLead = useMutation(api.leads.buyerMutations.cancelLead);
  return async () => {
    try {
      await cancelLead({ leadId });
    } catch (error) {
      handleError(error, { fallbackMessage: "Could not cancel request" });
      return false;
    }
    if (isFreeClaim) track("claim.withdrawn", { lead_id: leadId });
    toast.success("Request cancelled");
    return true;
  };
}

export function CancelRequestButton({
  leadId,
  leadNumber,
  isFreeClaim = false,
  variant = "ghost",
  className,
  asRow = false,
}: CancelRequestButtonProps) {
  const [open, setOpen] = useState(false);
  const confirmCancel = useConfirmCancel({ leadId, isFreeClaim });

  return (
    <>
      {asRow ? (
        <PhoneActionRowButton tone="destructive" onClick={() => setOpen(true)}>
          Cancel request
        </PhoneActionRowButton>
      ) : (
        <Button
          type="button"
          variant={variant}
          size="sm"
          className={className}
          onClick={() => setOpen(true)}
        >
          <X className="size-3.5" />
          Cancel request
        </Button>
      )}
      <ConfirmationDialog
        open={open}
        onOpenChange={setOpen}
        variant="destructive"
        title="Cancel this request?"
        description={leadNumber ? `Request ${leadNumber} closes.` : "The request closes."}
        confirmLabel="Cancel request"
        cancelLabel="Keep request"
        loadingLabel="Cancelling…"
        onConfirm={confirmCancel}
      />
    </>
  );
}
